import {
  CARROT_ROTATION_SPEED,
  Core,
  CoreConstructorParameters,
  CoreInitParameters,
  RABBIT_RESPAWN_SCALE_DURATION,
  RABBIT_RESPAWN_SCALE_EASE,
  RABBIT_STATE,
} from '@/modules/advergaming/core'
import {
  BoxGeometry,
  BufferGeometry,
  Group,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshToonMaterial,
  Object3D,
  Raycaster,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three'
import { Atom, atom, WritableAtom } from 'nanostores'
import gsap from 'gsap'
import { lerpDifficultyProperty } from '@/modules/advergaming/utils'

type Position = {
  index: number
  available: boolean
  isPlayer: boolean
}

const TL_LABEL_GROUND = 'ground'
const POSITIONS_COUNT = 9

const PARAMS = {
  interactionHelper: {
    radius: 1,
    visible: false,
  },
  difficulty: {
    lowDifficulty: {
      minLifespan: 0.5,
      maxLifespan: 2,
      minCombo: 5,
      peekabooChance: 0,
    },
    highDifficulty: {
      minLifespan: 0.25,
      maxLifespan: 1,
      minCombo: 20,
      peekabooChance: 0.25,
    },
    addEach: 5,
    minComboTrap: 5,
    trapChance: 0.5,
  },
  respaw: {
    delay: 2,
  },
}

/**
 * Utility function returning a 3D position from index.
 * The index being of of the position of the "holes" in which the carrots comes from.
 * It is used to compute the positions of the carrots as well as the holes.
 */
function getPositionFromIndex(index: number) {
  return {
    x: ((index % 3) - 1) * 2,
    y: 0,
    z: (Math.floor(index / 3) - 1) * 2,
  }
}

class CarrotManager {
  carrots: Carrot[] = []
  group = new Group()
  store: {
    $positions: WritableAtom<Position[]>
    $combo: Atom<number>
  }
  assets: Core['assets']

  constructor(
    $positions: WritableAtom<Position[]>,
    $combo: Atom<number>,
    assets: Core['assets']
  ) {
    this.store = {
      $positions,
      $combo,
    }

    this.assets = assets
  }

  update(deltaTime: number) {
    this.carrots.forEach(carrot => {
      carrot.update(deltaTime / 1000)
    })
  }

  public addCarrot() {
    const sleepingCarrot = this.carrots.find(
      carrot => carrot.state === null && carrot.canRespawn === false
    )

    if (sleepingCarrot) {
      sleepingCarrot.canRespawn = true
      sleepingCarrot.respawn()
    } else if (this.carrots.length < POSITIONS_COUNT - 1) {
      const carrot = new Carrot(this)
      this.group.add(carrot)
      this.carrots.push(carrot)
      carrot.respawn()
    }
  }

  /**
   * Return the index of an available position.
   * If no position is available, null is returned.
   */
  public getRandomAvailablePositionIndex(): number | null {
    const suffled = gsap.utils.shuffle([...this.store.$positions.get()])

    const availablePosition = suffled.find(entry => entry.available)

    if (availablePosition) {
      return availablePosition.index
    }

    return null
  }

  /**
   * Update the $positions atom availability at index.
   */
  public setPositionAvailability(
    index: number,
    available: boolean,
    isPlayer: boolean = false
  ) {
    const newPositions = this.store.$positions.get()

    newPositions.splice(index, 1, {
      index,
      available,
      isPlayer,
    })

    this.store.$positions.set(newPositions)
  }

  /**
   * Compute carrot lifespan based on combo click count. Progressively increasing difficulty.
   * This utility lerps the minLifespan and maxLifespan of both difficulty object
   * based on the current combo value and return a random value witin this interval.
   *
   * There is a small peekaboo chance that the carrot will have a lifespan of 0.
   * If combo is lower than lowDifficulty.minCombo, it will always only use the low difficulty object.
   * If combo is higher than lowDifficulty.minCombo and highDifficulty.minCombo, it will always lerp between both difficulty objects.
   * If combo is higher than highDifficulty.minCombo it will always only use the high difficulty object.
   */
  public computeCarrotLifespan() {
    const peekabooChance = lerpDifficultyProperty(
      PARAMS,
      'peekabooChance',
      this.store.$combo.get()
    )
    if (Math.random() < peekabooChance) {
      return PARAMS.difficulty.highDifficulty.minLifespan
    }

    const combo = this.store.$combo.get()

    if (combo < PARAMS.difficulty.lowDifficulty.minCombo) {
      return PARAMS.difficulty.lowDifficulty.maxLifespan
    } else if (combo < PARAMS.difficulty.highDifficulty.minCombo) {
      const t = MathUtils.mapLinear(
        combo,
        PARAMS.difficulty.lowDifficulty.minCombo,
        PARAMS.difficulty.highDifficulty.minCombo,
        0,
        1
      )

      const minLifespan = MathUtils.mapLinear(
        t,
        0,
        1,
        PARAMS.difficulty.lowDifficulty.minLifespan,
        PARAMS.difficulty.highDifficulty.minLifespan
      )

      const maxLifespan = MathUtils.mapLinear(
        t,
        0,
        1,
        PARAMS.difficulty.lowDifficulty.maxLifespan,
        PARAMS.difficulty.highDifficulty.maxLifespan
      )

      return MathUtils.randFloat(minLifespan, maxLifespan)
    }

    return MathUtils.randFloat(
      PARAMS.difficulty.highDifficulty.minLifespan,
      PARAMS.difficulty.highDifficulty.maxLifespan
    )
  }

  public computeCarrotIsTrap() {
    if (this.store.$combo.get() > PARAMS.difficulty.minComboTrap) {
      return Math.random() < PARAMS.difficulty.trapChance
    } else {
      return false
    }
  }

  public getCarrotAtIndex(index: number) {
    const carrot = this.carrots.find(c => {
      return c.state === 'ALIVE' && c.positionIndex === index
    })

    return carrot
  }
}

class Carrot extends Object3D {
  // Keeps reference to its "position index" in order to update the positions array when respawning
  public positionIndex?: number

  // Time since spawn
  public age = 0

  // For how long will the object be on screen
  public lifespan = 2

  // Delay between disposal and next respawn
  public delay = 1

  mesh: Mesh<BufferGeometry, MeshToonMaterial>

  // Store reference to entrance and exit animation
  animation?: gsap.core.Tween

  // Is it a carrot or a tube of dynamite?
  isTrap = false

  // Super naive "state machine"
  state: 'ALIVE' | 'DEAD' | null = null

  // When the user click on a stick of dynamite, we decrease the count of carrot back to 1.
  // Instead of removing all the other carrots at once, we wait that they expire and then do not respawn them.
  // The instance will then be reused when the diffuculty will increase again.
  canRespawn = true

  manager: CarrotManager

  carrot: Object3D

  dynamite: Object3D

  constructor(manager: CarrotManager) {
    super()

    this.respawn = this.respawn.bind(this)
    this.animateIn = this.animateIn.bind(this)
    this.animateOut = this.animateOut.bind(this)

    this.manager = manager

    this.visible = false

    this.mesh = new Mesh(
      new BoxGeometry(0.25, 0.25, 0.25),
      new MeshToonMaterial({
        color: 0x00ff00,
      })
    )

    this.carrot = this.manager.assets.get('carrot')!.clone()
    this.add(this.carrot)

    this.dynamite = this.manager.assets.get('dynamite')!.clone()
    this.add(this.dynamite)
  }

  update(deltaTime: number) {
    switch (this.state) {
      case 'ALIVE':
        this.age += deltaTime

        if (this.age > this.lifespan) {
          this.state = null
          this.animateOut()
        }
        break
      case 'DEAD':
        this.age += deltaTime

        // After some delay (cooldown), respawn it at new location.
        if (this.age > this.lifespan + this.delay) {
          // There shouldn't be any active animation at this point but kill it anyway to prevent potential overlapping tweens.
          this.animation?.kill()

          this.respawn()
        }
        break
      default:
        break
    }

    this.rotation.y += deltaTime * CARROT_ROTATION_SPEED * 1000
  }

  respawn() {
    this.isTrap = this.manager.computeCarrotIsTrap()
    this.mesh.material.color.set(this.isTrap ? '#ff0000' : '#00ff00')

    this.carrot.visible = !this.isTrap
    this.dynamite.visible = this.isTrap

    this.age = 0
    this.lifespan = this.manager.computeCarrotLifespan()
    this.visible = true

    // Try to find a new available position
    const newPositionIndex = this.manager.getRandomAvailablePositionIndex()
    if (typeof newPositionIndex === 'number') {
      // If we had a previous position, set it to available in the $positions atom
      if (
        typeof this.positionIndex === 'number' &&
        !this.manager.store.$positions.get()[this.positionIndex].isPlayer
      ) {
        this.manager.setPositionAvailability(this.positionIndex, true)
      }

      // Set the new position to unavailable in the $positions atom
      this.manager.setPositionAvailability(newPositionIndex, false)

      this.positionIndex = newPositionIndex

      const { x, y, z } = getPositionFromIndex(this.positionIndex)

      this.position.set(x, y, z)
    }

    this.animateIn()
  }

  animateIn() {
    // Carrot is considered alive even during spawn animation
    this.state = 'ALIVE'

    this.animation?.kill()
    this.animation = gsap.fromTo(
      this.scale,
      {
        x: 0,
        y: 0,
        z: 0,
      },
      {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.25,
      }
    )
  }

  animateOut() {
    this.animation?.kill()
    this.animation = gsap.to(this.scale, {
      x: 0,
      y: 0,
      z: 0,
      duration: 0.25,
      onComplete: () => {
        if (this.canRespawn) {
          this.state = 'DEAD'
        } else {
          this.state = null

          if (typeof this.positionIndex === 'number') {
            this.manager.setPositionAvailability(this.positionIndex, true)
          }
          this.positionIndex = undefined
        }
      },
    })
  }
}

class WhacACarrot extends Core {
  private pointer = new Vector2()
  private raycaster = new Raycaster()
  private interactiveGroup = new Group()

  private carrotManager!: CarrotManager

  private rabbit = new Group()
  // This is an empty object that we're going to use to smoothly animate the rabbit orientation.
  private rabbitLookAtHelper = new Object3D()
  private rabbitAnimation?: gsap.core.Timeline
  // This is cell index representing the rabbit position on the grid. 4 is the center cell.
  private rabbitPositionIndex = 4
  private rabbitLeftEar!: Object3D
  private rabbitRightEar!: Object3D

  protected cameraPositionDesktop: Vector3 = new Vector3(0, 5, 5)
  protected cameraPositionMobile: Vector3 = new Vector3(0, 5, 5)

  $positions: WritableAtom<Position[]>

  constructor(params: CoreConstructorParameters) {
    super(params)

    this.$positions = atom(
      Array.from({ length: POSITIONS_COUNT }, (_, i) => ({
        index: i,
        available: true,
        isPlayer: false,
      }))
    )
  }

  async init(params: CoreInitParameters) {
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerDown = this.onPointerDown.bind(this)

    this.carrotManager = new CarrotManager(
      this.$positions,
      this.store.$combo,
      this.assets
    )

    await super.init(params)

    this.scene.add(this.interactiveGroup)
    this.initGround()

    this.initRabbit()

    this.scene.add(this.carrotManager.group)
    this.carrotManager.addCarrot()

    this.containerEl.addEventListener('mousemove', this.onPointerMove)
    this.containerEl.addEventListener('pointerdown', this.onPointerDown)
  }

  onRender(interp: number, deltaTime: number) {
    this.carrotManager?.update(deltaTime)

    super.onRender(interp, deltaTime)
  }

  protected onFixedUpdate(timestep: number) {
    this.store.globalPinia.duration.whacACarrot += timestep
  }

  onPointerDown(e: MouseEvent) {
    this.store.$interactedWith.set(true)

    // Ignore click if rabbit is dead.
    if (this.rabbitState !== RABBIT_STATE.ALIVE) {
      return
    }

    this.updatePointer(e)

    const intersect = this.raycast(this.interactiveGroup.children)
    if (
      intersect?.object &&
      typeof intersect.object.userData.index === 'number'
    ) {
      const { index } = intersect.object.userData

      const animation = this.rabbitMoveTo(index)

      const carrot = this.carrotManager.getCarrotAtIndex(index)
      if (carrot) {
        animation.add(() => {
          this.onCarrotClicked(carrot)
        }, TL_LABEL_GROUND)
      }
    }
  }

  /**
   * Calculate pointer position in normalized device coordinates (-1 to +1) for both components.
   * Needs to be computed relative to the container, not the viewport.
   */
  onPointerMove(e: MouseEvent) {
    this.updatePointer(e)
    this.updateCSSPointer()
  }

  updateCSSPointer() {
    // Only update cursor style if player can actually interact with the game.
    if (this.rabbitState !== RABBIT_STATE.ALIVE) {
      this.containerEl.classList.toggle('has-pointer', false)

      return
    }

    const intersect = this.raycast(this.interactiveGroup.children)
    this.containerEl.classList.toggle('has-pointer', Boolean(intersect))
  }

  onCarrotClicked(carrot: Carrot) {
    carrot.visible = false
    carrot.age = carrot.lifespan
    carrot.state = 'DEAD'

    if (carrot.isTrap) {
      this.explodeRabbit()
    } else {
      this.eatCarrot(carrot)
    }
  }

  explodeRabbit() {
    this.rabbitState = RABBIT_STATE.DEAD

    this.updateCSSPointer()

    this.particlesDeadRabbit(this.rabbit.position)
    this.cameraShake(0.1)

    this.rabbit.visible = false

    this.store.$combo.set(0)

    // When the user clicks on a stick of dynamite we go back to a single active carrot
    this.carrotManager.carrots.forEach((carrot, index) => {
      carrot.canRespawn = index === 0
    })

    this.rabbitAnimation?.kill()
    this.rabbitAnimation = gsap
      .timeline({
        delay: PARAMS.respaw.delay,
      })
      .add(() => {
        this.rabbit.visible = true
      })
      .fromTo(
        this.rabbit.scale,
        {
          x: 0,
          y: 0,
          z: 0,
        },
        {
          x: 1,
          y: 1,
          z: 1,
          ease: RABBIT_RESPAWN_SCALE_EASE,
          duration: RABBIT_RESPAWN_SCALE_DURATION,
          onComplete: () => {
            this.rabbitState = RABBIT_STATE.ALIVE
          },
        }
      )
  }

  eatCarrot(carrot: Carrot) {
    this.particlesCarrot(carrot.position)

    this.store.$combo.set(this.store.$combo.get() + 1)
    this.store.$score.set(this.store.$score.get() + 1)

    if (this.store.$combo.get() % PARAMS.difficulty.addEach === 0) {
      this.carrotManager.addCarrot()
    }
  }

  rabbitMoveTo(index: number) {
    if (
      this.rabbitAnimation?.isActive() &&
      this.rabbitPositionIndex !== index
    ) {
      this.onCancelRabbitMove()
    }

    const rabbitPositionYZero = this.rabbit.position.clone()
    rabbitPositionYZero.y = 0

    // Spawn particles at y 0 otherwise it looks weird when player spam jump on same cell and we spawn particles dust mid air.
    this.particlesDustPoof(rabbitPositionYZero)

    if (index !== this.rabbitPositionIndex) {
      // Set previous rabbit position to available
      this.carrotManager.setPositionAvailability(this.rabbitPositionIndex, true)

      // Set new rabbit position  to unavailable
      this.rabbitPositionIndex = index
      this.carrotManager.setPositionAvailability(
        this.rabbitPositionIndex,
        false,
        true
      )
    }

    const position = getPositionFromIndex(index)

    // Move the helper object to the rabbit position before the jump, update its target by calling lookAt to the target position.
    // Use the y 0 position otherwise it will introduce weird behaviours when user spam jump on same cell. Rabbit will roll over trying to look underneath itself.
    this.rabbitLookAtHelper.position.copy(rabbitPositionYZero)
    this.rabbitLookAtHelper.lookAt(
      new Vector3(position.x, position.y, position.z)
    )

    this.rabbitAnimation?.kill()

    const DURATION = 0.5
    this.rabbitAnimation = gsap
      .timeline()
      .fromTo(
        this.rabbit.scale,
        {
          x: 1.5,
          y: 0.5,
        },
        {
          x: 1,
          y: 1,
          ease: 'power4.out',
          duration: DURATION,
        },
        0
      )
      .to(
        this.rabbit.position,
        {
          x: position.x,
          z: position.z,

          keyframes: {
            y: [0, 1, 0],
            ease: 'linear',
          },
          ease: 'power2.inOut',
          duration: DURATION * 0.6,
          // Smoothly transition the orientation of the rabbit towards target.
          // During the animation, lerp the rabbit quaternion to the helper quaternion.
          onUpdate: () => {
            if (!this.rabbitAnimation) {
              return
            }

            this.rabbit.quaternion.slerp(
              this.rabbitLookAtHelper.quaternion,
              this.rabbitAnimation.progress()
            )
          },
        },
        0
      )
      .addLabel(TL_LABEL_GROUND, '-=0.25')
      // Dust poof when rabbit lands
      .add(() => {
        this.particlesDustPoof(new Vector3(position.x, position.y, position.z))
      }, TL_LABEL_GROUND)

    return this.rabbitAnimation
  }

  /**
   * Check the distance from current position to previous move target point.
   * If distance between them is close enough and there is a carrot at previous target point, rabbit eat the carrot.
   * This mitigate issues where rabbit has visually completed the move (but the timeline is not completed), then player clicks on another spot
   * and rabbit don't eat the carrot at previous spot even if it has visually landed on it.
   */
  onCancelRabbitMove() {
    const targetPosition = getPositionFromIndex(this.rabbitPositionIndex)
    const distance = this.rabbit.position.distanceTo(
      new Vector3(targetPosition.x, targetPosition.y, targetPosition.z)
    )

    const carrot = this.carrotManager.getCarrotAtIndex(this.rabbitPositionIndex)

    if (carrot && distance <= 1.35) {
      this.onCarrotClicked(carrot)
    }
  }

  updatePointer(e: MouseEvent) {
    const BCR = this.containerEl.getBoundingClientRect()
    this.pointer.x = ((e.clientX - BCR.left) / BCR.width) * 2 - 1
    this.pointer.y = -((e.clientY - BCR.top) / BCR.height) * 2 + 1
  }

  raycast(objects: Object3D[]) {
    this.raycaster.setFromCamera(this.pointer, this.camera)
    const intersects = this.raycaster.intersectObjects(objects)

    if (intersects.length <= 0) {
      return
    }

    return intersects[0]
  }

  initGround() {
    const holeMesh = this.assets.get('tile-hole-green')!

    const interactiveHelperMesh = new Mesh(
      new BoxGeometry(2, 0.1, 2),
      new MeshBasicMaterial({
        color: 0x00ff00,
        wireframe: true,
        visible: false,
      })
    )

    for (let i = 0; i < POSITIONS_COUNT; i++) {
      const { x, y, z } = getPositionFromIndex(i)

      // This mesh is purely visual
      const clone = holeMesh.clone()
      clone.position.set(x, y, z)

      this.scene.add(clone)

      // This mesh is hidden and interactive
      const interactiveHelperClone = interactiveHelperMesh.clone()
      interactiveHelperClone.position.set(x, y, z)
      // This is required in order to know the position of object3d when clicked
      interactiveHelperClone.userData.index = i
      this.interactiveGroup.add(interactiveHelperClone)
    }

    const left = this.assets.get('tile')!.clone()
    left.position.set(-6, 0, 0)
    left.scale.set(3, 1, 6)
    this.scene.add(left)

    const right = left.clone()
    right.position.set(6, 0, 0)
    this.scene.add(right)

    const top = left.clone()
    top.position.set(0, 0, -6)
    top.scale.set(3, 1, 3)
    this.scene.add(top)

    const bottom = top.clone()
    bottom.position.set(0, 0, 6)
    this.scene.add(bottom)
  }

  initRabbit() {
    this.rabbit.add(this.assets.get('rabbit')!.clone())
    this.scene.add(this.rabbit)

    this.rabbitLeftEar = this.rabbit.getObjectByName('earLeft')!
    this.rabbitRightEar = this.rabbit.getObjectByName('earRight')!

    const earTweensParams = {
      from: {
        x: 0.05,
      },
      to: {
        x: -0.1,
        ease: 'sine.out',
        yoyoEase: 'sine.in',
        yoyo: true,
        repeat: -1,
        duration: 1,
      },
    }

    gsap.fromTo(
      this.rabbitLeftEar.rotation,
      earTweensParams.from,
      earTweensParams.to
    )

    gsap.fromTo(this.rabbitRightEar.rotation, earTweensParams.from, {
      delay: 0.05,
      ...earTweensParams.to,
    })

    gsap.fromTo(
      this.rabbit.scale,
      {
        x: 1,
        y: 1,
        z: 1,
      },
      {
        x: 1.025,
        y: 0.95,
        z: 1.025,
        ease: 'sine.out',
        yoyo: true,
        repeat: -1,
        duration: 1,
      }
    )

    // Set initial rabit position to unavailable
    this.carrotManager.setPositionAvailability(
      this.rabbitPositionIndex,
      false,
      true
    )
  }

  dispose() {
    super.dispose()

    this.containerEl.removeEventListener('mousemove', this.onPointerMove)
    this.containerEl.removeEventListener('pointerdown', this.onPointerDown)
  }

  protected initDebug(): void {
    super.initDebug()

    if (!this.debug) {
      return
    }

    const difficulty = this.pane.addFolder({
      title: 'difficulty',
      expanded: true,
    })

    const lowDifficulty = difficulty.addFolder({
      title: 'low',
      expanded: true,
    })
    lowDifficulty.addBinding(PARAMS.difficulty.lowDifficulty, 'minCombo', {
      step: 1,
      min: 0,
    })
    lowDifficulty.addBinding(PARAMS.difficulty.lowDifficulty, 'minLifespan', {
      min: 0,
      max: 5,
    })
    lowDifficulty.addBinding(PARAMS.difficulty.lowDifficulty, 'maxLifespan', {
      min: 0,
      max: 5,
    })

    const highDifficulty = difficulty.addFolder({
      title: 'high',
      expanded: true,
    })
    highDifficulty.addBinding(PARAMS.difficulty.highDifficulty, 'minCombo', {
      step: 1,
      min: 0,
    })
    highDifficulty.addBinding(PARAMS.difficulty.highDifficulty, 'minLifespan', {
      min: 0,
      max: 5,
    })
    highDifficulty.addBinding(PARAMS.difficulty.highDifficulty, 'maxLifespan', {
      min: 0,
      max: 5,
    })

    difficulty.addBinding(PARAMS.difficulty, 'minComboTrap', {
      min: 0,
      step: 1,
    })

    difficulty.addBinding(PARAMS.difficulty, 'trapChance', {
      min: 0,
      max: 1,
    })

    difficulty.addBinding(PARAMS.difficulty, 'addEach', {
      min: 0,
      step: 1,
    })
  }
}

export { WhacACarrot }
